/* ============================================================================
   gaela-marco.js — el comportamiento del marco al bajar.

   Un solo listener de scroll para el encabezado y el widget: son la misma
   pregunta («¿ya bajamos?») y dos listeners cuestan el doble por nada.

   Solo se añaden y se quitan clases; el aspecto entero vive en el CSS.
   Con prefers-reduced-motion no arranca: el CSS ya entrega los dos estados
   finales, que es lo correcto (D-040).
   ========================================================================= */
(function () {
  'use strict';

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* 🔴 DOS UMBRALES, NO UNO. HISTÉRESIS.
     Historia: primero fue 24 y dejaba una franja muerta —la barra seguía
     transparente sobre contenido que ya se movía—; después se bajó a 0, que
     es la pregunta correcta («¿estamos arriba del todo?») y trajo un defecto
     peor: **el umbral quedó clavado justo donde la página descansa**. Con el
     dedo bajando despacio, el scroll cruza esa raya varias veces por un píxel
     —y en iOS el rebote elástico da hasta valores negativos—, así que la
     barra se encendía y se apagaba sola, cada vez con su transición entera.
     Textual del usuario: «cuando hago scroll lento el header se traba, se
     abre y se cierra rápidamente».

     La cura es la de siempre para un interruptor que tiembla: **entrar y
     salir por sitios distintos**. Se pone sólida al pasar de 10 px y no
     vuelve a transparente hasta bajar de 2. Entre los dos no pasa nada, que
     es exactamente lo que se quiere: ahí es donde tiembla el dedo.
     ⚠️ Los 10 px siguen cumpliendo lo que se pidió en 2026-09-08 —«apenas
     sienta el primer scroll»—: son imperceptibles, y es el mismo número que
     usa el ojo de la barra de compra. */
  var ENTRA = 10;   /* de transparente a sólida */
  var SALE  = 2;    /* y sólo al revés por debajo de aquí */

  function arrancar() {
    var encabezado = document.querySelector('.gaela-encabezado');
    var widget = document.querySelector('.gaela-widget');
    if (!encabezado && !widget) return;

    var pedido = false;
    var abajoAntes = null;

    /* 🔴 CUÁNTO SE HA BAJADO NO SIEMPRE LO SABE `window`. (A-059 · contrato C-12)
       A partir de 990 px Horizon deja de hacer scroll en el documento y lo pasa
       a `.page-wrapper`. Ahí `window.scrollY` se queda clavado en 0 para
       siempre, así que el encabezado NUNCA encogía en escritorio: no fallaba
       nada, simplemente no pasaba nada. Se pregunta al que de verdad se mueve. */
    function bajado() {
      var caja = document.querySelector('.page-wrapper');
      if (caja && caja.scrollHeight > caja.clientHeight + 1) return caja.scrollTop;
      return window.scrollY || document.documentElement.scrollTop || 0;
    }

    function mirar() {
      /* El umbral que se aplica depende de dónde estemos YA: por eso es
         histéresis y no un redondeo. */
      var y = bajado();
      var abajo = abajoAntes === true ? y > SALE : y > ENTRA;
      if (abajo !== abajoAntes) {
        abajoAntes = abajo;
        if (encabezado) encabezado.classList.toggle('gaela-encabezado--solido', abajo);
        if (widget) widget.classList.toggle('gaela-widget--compacto', abajo);
      }
      pedido = false;
    }


    /* En fase de captura, por lo mismo: los `scroll` de un elemento no burbujean
       y un escuchador en `window` no se entera de los de `.page-wrapper`. */
    document.addEventListener('scroll', function () {
      if (!pedido) { pedido = true; requestAnimationFrame(mirar); }
    }, { passive: true, capture: true });

    mirar();
  }

  /* ══ ABRIR Y CERRAR UN DIÁLOGO SIN CONGELAR LA PÁGINA (2026-10-18) ══════════
     🔴 `showModal()` COSTABA 180-250 ms DE INP. Un diálogo modal vuelve inerte
     TODO el documento, y Chrome recalcula el estilo de cada elemento antes de
     pintar. Medido en la ficha, celular con CPU 4×: el ☰ tardaba 170-360 ms en
     abrir y la foto a pantalla completa 190-340, casi todo dentro de esa llamada.
     Ni el desenfoque, ni Releasit, ni el bloqueo de scroll movían el número.

     Con la API Popover la pieza sube igual a la capa superior —mismo aspecto,
     mismo ::backdrop con su desenfoque, comprobado píxel a píxel— pero sin
     volver inerte la página: el ☰ baja a 40-64 ms.

     ⚠️ POPOVER «manual», NO «auto». El «auto» cierra al tocar fuera, pero ese
        toque SIGUE hasta lo que hay debajo (una foto abriría el visor). El modal
        no dejaba pasar nada, así que aquí se hace igual: un toque fuera cierra
        y se queda ahí. Esc también se escucha aquí.
     ⚠️ Sin Popover (iOS 16 o anterior) se usa `showModal()`, como siempre.
     ⚠️ El popover no atrapa el foco como el modal: se lleva el foco dentro al
        abrir y se devuelve al botón al cerrar. */
  var POPOVER = typeof HTMLElement !== 'undefined' &&
                Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover');
  var volverA = null;

  function abierta(d) {
    return !!(d && (d.open || (POPOVER && d.matches(':popover-open'))));
  }
  function abreDialogo(d) {
    if (abierta(d)) return;
    volverA = document.activeElement;
    if (!POPOVER) { d.showModal(); return; }
    if (d.popover !== 'manual') d.popover = 'manual';
    d.showPopover();
    /* lo mismo que hace showModal(): el foco al primer control de dentro */
    var f = d.querySelector('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
  }
  function cierraDialogo(d) {
    if (!abierta(d)) return;
    if (POPOVER && d.matches(':popover-open')) d.hidePopover(); else d.close();
  }
  /* 🔴 LA PRIMERA APERTURA SE PAGA EN UN MOMENTO OCIOSO (2026-10-19, INP).
     Medido con CPU 4×: la PRIMERA vez que la hoja del ☰ entra en la capa
     superior cuesta 60–85 ms de estilo y maquetación forzados —aunque tenga 7
     elementos, y no depende de ninguna hoja CSS: se probó quitándolas—; las
     siguientes, 2–8 ms. Era el toque más lento de la ficha. Aquí se abre y se
     cierra en la MISMA tarea cuando el navegador está libre: nunca llega a
     pintarse, y la primera apertura de verdad queda en ~12 ms.
     Sólo si no hay ningún diálogo abierto, y sólo con popover (el camino de
     `abreDialogo()`). El `toggle` que dispara llega ya cerrado: el oyente de
     devolver el foco no hace nada porque `volverA` está vacío. */
  function calienta(d) {
    if (!POPOVER || !d) return;
    var hazlo = function () {
      if (abierta(d) || abiertos().length) return;
      if (d.popover !== 'manual') d.popover = 'manual';
      d.showPopover();
      d.hidePopover();
    };
    var cuando = function () {
      if (window.requestIdleCallback) requestIdleCallback(hazlo, { timeout: 3000 });
      else setTimeout(hazlo, 1500);
    };
    if (document.readyState === 'complete') cuando();
    else addEventListener('load', cuando);
  }
  /* `close` para el modal; `toggle` para el popover. */
  function alCerrar(d, fn) {
    d.addEventListener('close', fn);
    d.addEventListener('toggle', function (e) { if (e.newState === 'closed') fn(); });
  }
  function abiertos() {
    return [].filter.call(document.querySelectorAll('dialog[popover]'), abierta);
  }
  if (POPOVER && !document.documentElement.dataset.gfDialogos) {
    document.documentElement.dataset.gfDialogos = '1';
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var a = abiertos();
      if (a.length) { e.preventDefault(); cierraDialogo(a[a.length - 1]); }
    });
    /* En captura: llega antes que el elemento tocado, y así se lo come. */
    document.addEventListener('click', function (e) {
      var a = abiertos();
      if (!a.length) return;
      var d = a[a.length - 1];
      if (d.contains(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      cierraDialogo(d);
    }, true);
    document.addEventListener('toggle', function (e) {
      if (e.newState !== 'closed' || abiertos().length) return;
      if (volverA && volverA.focus) { try { volverA.focus({ preventScroll: true }); } catch (x) {} }
      volverA = null;
    }, true);
  }

  /* La hoja del ☰. <dialog> nativo, abierto con `abreDialogo()`. */
  function hoja() {
    var abre = document.querySelector('[data-gaela-abre-hoja]');
    var caja = document.getElementById('gaela-hoja');
    if (!abre || !caja || typeof caja.showModal !== 'function') return;

    abre.addEventListener('click', function () { abreDialogo(caja); });
    calienta(caja);
    caja.addEventListener('click', function (e) {
      // tocar fuera de la caja cierra
      if (e.target === caja) cierraDialogo(caja);
    });
    var cerrar = caja.querySelector('[data-gaela-cierra-hoja]');
    if (cerrar) cerrar.addEventListener('click', function () { cierraDialogo(caja); });
  }

  /* El widget no puede tapar el pie: ahí es donde están los legales y el
     descargo, que son justo lo que no se puede estorbar. Cuando el pie entra
     en pantalla el widget se retira hacia abajo; al salir, vuelve.
     IntersectionObserver nativo: ni un listener de scroll más. */
  function cederAlPie() {
    var widget = document.querySelector('.gaela-widget');
    var pie = document.querySelector('.gaela-pie');
    if (!widget || !pie || typeof IntersectionObserver !== 'function') return;
    if (widget.dataset.gaelaCede === '1') return;
    widget.dataset.gaelaCede = '1';

    new IntersectionObserver(function (entradas) {
      widget.classList.toggle('gaela-widget--oculto', entradas[0].isIntersecting);
    }, { rootMargin: '0px 0px -10% 0px' }).observe(pie);
  }

  /* La hoja de la BOLSA. Mismo diálogo que el menú, con dos diferencias que
     importan:
       · sólo en celular — en escritorio la bolsa sigue llevando a `/cart`,
         que es lo que eligió el usuario;
       · el gancho es un ENLACE de verdad, así que si el script no corre o la
         pantalla es ancha, el clic hace lo que hacía siempre: navegar. Nunca
         se queda sin carrito por un error de JS. */
  function bolsa() {
    var abre = document.querySelector('[data-gaela-abre-bolsa]');
    var caja = document.getElementById('gaela-bolsa');
    if (!abre || !caja || typeof caja.showModal !== 'function') return;

    var estrecho = matchMedia('(max-width: 749px)');

    abre.addEventListener('click', function (e) {
      if (!estrecho.matches) return;   // en escritorio, que navegue
      e.preventDefault();
      abreDialogo(caja);
    });
    caja.addEventListener('click', function (e) { if (e.target === caja) cierraDialogo(caja); });
    if (estrecho.matches) calienta(caja);   // sólo en celular: en escritorio la bolsa navega
    var cerrar = caja.querySelector('[data-gaela-cierra-hoja]');
    if (cerrar) cerrar.addEventListener('click', function () { cierraDialogo(caja); });
    /* Si se ensancha la ventana con la hoja abierta, se cierra: en escritorio
       esa hoja no existe y quedaría un diálogo huérfano tapando la página. */
    estrecho.addEventListener('change', function (e) { if (!e.matches) cierraDialogo(caja); });

    /* ══ CAMBIAR CANTIDADES SIN SALIR DE LA PÁGINA ═════════════════════════
       Pedido (2026-09-21): «si le doy a quitar me lleva a otra página y dice
       page not found; que la hoja funcione bien».

       Eran dos fallos. El del 404 se arregla en el Liquid (la dirección venía
       vacía). El otro es este: los enlaces navegaban a `/cart`, así que tocar
       una cantidad te sacaba de la ficha. Ahora se llama al carrito por
       detrás y se vuelve a pintar SÓLO la hoja, con la respuesta del
       servidor: el total, el escalón de ahorro y el número de la bolsa salen
       del tema, no se recalculan aquí (si no, habría dos verdades).

       ⚠️ Sin JavaScript los enlaces siguen siendo enlaces y llevan a `/cart`,
       que es lo que hacían antes: se degrada, no se rompe. */
    var seccion = caja.closest('.shopify-section');
    var idSeccion = seccion ? seccion.id.replace('shopify-section-', '') : null;
    var ocupada = false;

    /* 🔴 EL NÚMERO SE PINTA, NO SE REEMPLAZA EL ENLACE. La bolsa del
       encabezado lleva colgado el manejador que abre la hoja; si se cambiara
       el elemento entero, ese manejador moriría y tocar la bolsa dejaría de
       abrirla. Por eso sólo se toca el numerito, y se crea si no estaba —con
       la bolsa vacía Liquid no lo pinta—. */
    function pintaCuenta(n) {
      document.querySelectorAll('.gaela-encabezado__bolsa, .gaela-encabezado__bolsa-cel').forEach(function (b) {
        var s = b.querySelector('.gaela-carrito__n');
        if (!n) { if (s) s.remove(); return; }
        if (!s) {
          s = document.createElement('span');
          s.className = 'gaela-carrito__n';
          b.appendChild(s);
        }
        s.textContent = n;
      });
    }

    function repinta(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var nueva = doc.querySelector('#gaela-bolsa .gf-car');
      var vieja = caja.querySelector('.gf-car');
      if (nueva && vieja) vieja.replaceWith(nueva);
      var s = doc.querySelector('.gaela-carrito__n');
      var n = s ? parseInt(s.textContent, 10) || 0 : 0;
      ultimo = n;
      try { localStorage.setItem('gaela_bolsa', String(n)); } catch (e) {}
      pintaCuenta(n);
    }

    /* ══ AL VOLVER, EL NÚMERO SE PONE AL DÍA ═══════════════════════════════
       Pedido (2026-09-21): «la bolsa se actualiza, sólo que me toca reiniciar
       la página para que se vea».

       El porqué: al volver del checkout con el botón de atrás, el navegador
       **no vuelve a pedir la página**, la saca de su caché tal y como estaba
       —con el número viejo pintado—. Eso es el bfcache, y es deseable: por eso
       volver es instantáneo.

       La solución no es desactivarlo, es preguntar por el carrito al volver.
       `/cart.js` son unos cientos de bytes y no toca nada más de la página.
       ⚠️ `pageshow` con `persisted` es el aviso de que la página viene de la
       caché; `visibilitychange` cubre volver desde otra pestaña. */
    var ultimo = null;
    /* 🔴 PRIMERO EL APUNTE, DESPUÉS EL SERVIDOR (2026-09-21). El número se
       corrige al instante con lo que se anotó al ir al checkout, y la
       consulta al carrito sólo lo confirma. Así el contador no depende de que
       `/cart.js` responda —hoy mismo devolvió 429 de tanto probar— ni de la
       red del móvil. */
    function apunte() {
      try {
        var v = localStorage.getItem('gaela_bolsa');
        if (v === null) return;
        var n = parseInt(v, 10);
        if (!isNaN(n) && n !== ultimo) { ultimo = n; pintaCuenta(n); }
      } catch (e) {}
    }

    function refrescaCuenta() {
      apunte();
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js',
        { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          try { localStorage.setItem('gaela_bolsa', String(c.item_count)); } catch (e) {}
          if (c.item_count === ultimo) return;
          ultimo = c.item_count;
          pintaCuenta(c.item_count);
        })
        .catch(function () {});
    }
    /* ⚠️ Y EN CADA APARICIÓN, NO SÓLO CUANDO VIENE DEL BFCACHE. Medido:
       al volver del checkout el navegador sirvió la ficha desde su caché
       normal —`persisted` en false— y el número se quedó en 6 con la bolsa
       ya en 2. Preguntar siempre cuesta una petición de unos cientos de
       bytes y sólo repinta si el número cambió. */
    addEventListener('pageshow', function () { refrescaCuenta(); });
    addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refrescaCuenta();
    });

    caja.addEventListener('click', function (e) {
      var a = e.target.closest('.gf-car__mas, .gf-car__quita');
      if (!a || !idSeccion) return;
      var linea = a.dataset.gfLinea;
      var cant = a.dataset.gfCant;
      if (linea === undefined || cant === undefined) return;   // sin datos, que navegue

      /* 🔴 EL `preventDefault` VA ANTES QUE CUALQUIER SALIDA (2026-09-21).
         Pedido: «si voy rápido me lleva a otra página, literal me cambia de
         página». Era esto: mientras una petición estaba en marcha, el
         manejador hacía `return` SIN cancelar el clic, así que el navegador
         seguía el enlace y se iba a `/cart`. Ahora primero se cancela y
         después se decide. */
      e.preventDefault();
      if (ocupada) return;
      ocupada = true;
      caja.setAttribute('data-gaela-esperando', '');

      /* 🔴 UN SOLO VIAJE AL SERVIDOR, NO DOS. El carrito de Shopify acepta el
         parámetro `sections` y devuelve, en la MISMA respuesta, el carrito
         nuevo y la sección ya pintada. Antes eran dos idas y venidas
         —cambiar, y luego pedir la sección—, y en un móvil con 4G eso es lo
         que se sentía como lentitud. */
      var raiz = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(raiz + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: Number(linea),
          quantity: Number(cant),
          sections: idSeccion,
          sections_url: location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j && j.sections && j.sections[idSeccion]) repinta(j.sections[idSeccion]);
          else throw new Error('sin sección');
        })
        .catch(function () { location.href = a.href; })   // si algo falla, el camino de siempre
        .then(function () {
          ocupada = false;
          caja.removeAttribute('data-gaela-esperando');
        });
    });
  }

  /* ══ LOS CARRILES ═══════════════════════════════════════════════════════════
     Quietos se paran en el canalillo; se abren de canto a canto mientras se
     desliza y se cierran al soltar. El aspecto vive entero en el CSS
     (`[data-gf-riel]`): aquí sólo se marca qué es carril y cuándo se mueve.

     🔴 SE MARCA MIDIENDO, NO POR CLASE. Un carril sólo necesita recorte si
     de verdad llega al canto de la pantalla; en escritorio los mismos carriles
     viven dentro del contenedor centrado y ahí recortar se comería contenido.
     Se mira su rectángulo, y se vuelve a mirar al cambiar de tamaño.

     ⚠️ La lista de selectores es explícita a propósito: olfatear `overflow-x`
     obliga a recorrer todos los elementos de la página, y esto corre en todas.
     Un carril nuevo entra añadiéndose aquí o marcándose `data-gf-riel-auto`. */
  function rieles() {
    var SEL = '.gf-riel, .gf-ev-gal, .gf-ev-banda__carril, .gf-h2-carril,' +
              ' .gf-h2-piezas, [data-gf-riel-auto]';
    var lista = [].slice.call(document.querySelectorAll(SEL));
    if (!lista.length) return;

    function revisa() {
      var ancho = document.documentElement.clientWidth;
      lista.forEach(function (c) {
        var r = c.getBoundingClientRect();
        if (r.left <= 1 && r.right >= ancho - 1) c.setAttribute('data-gf-riel', '');
        else c.removeAttribute('data-gf-riel');
      });
    }

    lista.forEach(function (c) {
      var t = null;
      c.addEventListener('scroll', function () {
        c.setAttribute('data-gf-riel-abierto', '');
        clearTimeout(t);
        /* 220 ms tras la última sacudida se cierra: es lo que tarda la inercia
           en calmarse sin que el recorte parpadee a mitad del gesto. */
        t = setTimeout(function () { c.removeAttribute('data-gf-riel-abierto'); }, 220);
      }, { passive: true });
    });

    revisa();
    addEventListener('resize', revisa);
    addEventListener('load', revisa);
  }

  /* ══ LOS VÍDEOS QUE ARRANCAN SOLOS ═══════════════════════════════════════════════
     Pedido (2026-09-18): *«que se reproduzcan apenas ingresan, y si no se
     activa, apenas se haga el scroll»*. Vale para cualquier `video.gf-auto`
     de cualquier página (hoy, el vídeo de la galería de la ficha). Los
     REELS no llevan esa clase: los dejó fuera expresamente.
       1 · al cargar;
       2 · con el PRIMER gesto —scroll, toque, clic, tecla—, para los
           navegadores que sólo sueltan la reproducción tras una interacción;
       3 · cada vez que entra en pantalla.
     ⚠️ El scroll se escucha en CAPTURA sobre el documento: en escritorio se
     desplaza `.page-wrapper` y sus `scroll` no burbujean (C-12, A-059). */
  function autovideos() {
    var vids = [].slice.call(document.querySelectorAll('video.gf-auto'));
    if (!vids.length) return;
    /* 🔴 LA CALIDAD SE ELIGE AQUÍ, NO EN EL MARCADO. El ancho de la ventana
       manda: en un teléfono la pieza no pasa de 400 px y la copia de 480p se
       ve idéntica pesando la mitad. Y si el navegador declara una conexión
       lenta o el ahorro de datos, la ligera también, sea cual sea la
       pantalla. */
    function fuente(v) {
      if (v.src || !v.dataset.gfHd) return;
      var hd = v.dataset.gfHd;
      var c = navigator.connection || {};
      var pobre = c.saveData === true || /(^|-)2g$|^3g$/.test(c.effectiveType || '');
      /* La copia ligera se deduce del nombre de la grande. Si esa direccion no
         existiera, el propio video avisa con `error` y se vuelve a la buena:
         nunca se queda en negro. */
      var sd = hd.replace(/HD-\d+p-[0-9.]+Mbps/, 'SD-480p-1.0Mbps');
      if (sd !== hd && (pobre || (innerWidth || 0) < 1000)) {
        v.addEventListener('error', function vuelve() {
          v.src = hd;
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        }, { once: true });
        v.src = sd;
      } else {
        v.src = hd;
      }
    }

    function intenta(v) {
      /* parado a mano, o hay otro sonando: no se arranca (ver `enSitio()`) */
      if (v.dataset.gfParado) return;
      if ([].some.call(document.querySelectorAll(VIDEOS), function (o) { return !o.paused && !o.muted; })) return;
      fuente(v);
      v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    /* 🔴 SÓLO SE ARRANCA LO QUE SE VE (2026-09-21). Con `preload="none"` el
       vídeo no pesa nada hasta que alguien lo reproduce, así que arrancarlos
       todos al cargar volvería a bajar los 4,3 MB de golpe. Se arranca lo que
       está en pantalla; del resto se encarga el observador de más abajo. */
    function seVe(v) {
      var r = v.getBoundingClientRect();
      return r.bottom > 0 && r.top < (innerHeight || 0) + 100 && r.width > 0;
    }
    /* ⚠️ Y NO DURANTE LA CARGA: el clip de arriba pesa 2,1 MB y, arrancándolo
       ya, le quitaba el ancho de banda al texto y a la foto —medido: el LCP
       subía de 3,4 s a 4,2 s—. Se espera a que la página termine de cargar; el
       vídeo sigue arrancando solo, un instante después. */
    function arrancaLoVisible() {
      vids.forEach(function (v) { if (seVe(v)) intenta(v); });
    }
    /* 🔴 EN CELULAR NO ARRANCA SOLO AL CARGAR: ESPERA AL PRIMER GESTO
       (2026-09-21). Medido en PageSpeed móvil: el LCP daba **9,5 s** y el
       elemento era el vídeo —sus 1.107 KB tardan 7-9 s en una 4G mala, y el
       primer fotograma, al pintarse, se convierte en el elemento más grande—.

       Al arrancarlo con el primer scroll o toque pasan tres cosas: el LCP
       vuelve a ser el póster (unos 3 s), quien rebota sin tocar nada no gasta
       1,1 MB de datos, y en la práctica el vídeo se sigue viendo solo, porque
       en un teléfono el primer scroll llega en menos de un segundo.

       ⚠️ En ESCRITORIO se mantiene el arranque automático: ahí la nota ya es
       95, la conexión aguanta y el vídeo es parte de la galería.
       ⚠️ Los oyentes del primer gesto (scroll, toque, tecla) ya existían más
       arriba: esto sólo decide si ADEMÁS se arranca al cargar. */
    var celular = matchMedia('(max-width: 749px)').matches;
    /* 🔴 LA PUERTA. En celular nada arranca hasta el primer gesto, y eso
       incluye al OBSERVADOR de más abajo: el vídeo de arriba ya está en
       pantalla al cargar, así que el observador lo arrancaba igual y los
       1.107 KB volvían a bajar. Medido: el peso no se movía de 3,26 MB. */
    var puertaAbierta = !celular;
    if (!celular) {
      if (document.readyState === 'complete') setTimeout(arrancaLoVisible, 200);
      else addEventListener('load', function () { setTimeout(arrancaLoVisible, 200); });
    }
    var EV = ['scroll', 'touchstart', 'pointerdown', 'keydown'];
    function reintenta() {
      puertaAbierta = true;
      vids.forEach(function (v) { if (v.paused && seVe(v)) intenta(v); });
      EV.forEach(function (ev) { document.removeEventListener(ev, reintenta, true); });
    }
    EV.forEach(function (ev) {
      document.addEventListener(ev, reintenta, { passive: true, capture: true });
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!puertaAbierta) return;   // en celular, hasta el primer gesto no se baja un byte
          if (e.isIntersecting && e.target.paused) intenta(e.target);
        });
      }, { threshold: 0.25 });
      vids.forEach(function (v) { io.observe(v); });
    }
  }


  /* ══ EL VISOR A PANTALLA COMPLETA ═════════════════════════════════════════
     Pedido textual (2026-09-18): «las imágenes y vídeos del hero, que si les
     hago clic no se haga zoom, que se pongan en la pantalla completa con una
     X para cerrar; lo mismo con la sección de reels».

     🔴 UN SOLO VISOR PARA TODO. Es el mismo objeto —una pieza que se abre a
     pantalla completa— en la galería y en los reels, así que es UN `<dialog>`
     que se crea la primera vez y se reutiliza (Regla 18). No toca el marcado
     de ninguna sección: escucha el clic por delegación.

     `<dialog>` nativo: Esc, el foco atrapado y el fondo inerte los pone el
     navegador. La X y un clic fuera de la pieza también cierran.

     ⚠️ La foto se pide GRANDE (2000 px): la de la página viene al ancho de su
     hueco y a pantalla completa se vería blanda. El vídeo se abre con sonido
     y controles: el clic del usuario es el gesto que el navegador exige. */
  /* 🔴 SOLO FOTOS (2026-10-18). Pedido: «los videos hagamos que no se abra una
     ventana, si no que solo reproduzca desde ahí». Los vídeos los lleva
     `enSitio()`, más abajo. La rama de vídeo de `abre()` se queda viva:
     devolver los dos selectores a SEL la recupera. */
  function visor() {
    var SEL = '.gf-ev-gal img, img.gf-ev-reel__v';
    var dlg = null;
    var caja = null;

    function crea() {
      dlg = document.createElement('dialog');
      dlg.className = 'gf-visor';
      dlg.setAttribute('aria-label', 'Vista a pantalla completa');
      dlg.innerHTML =
        '<button type="button" class="gf-visor__x" aria-label="Cerrar">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<div class="gf-visor__caja"></div>';
      caja = dlg.querySelector('.gf-visor__caja');
      document.body.appendChild(dlg);
      dlg.querySelector('.gf-visor__x').addEventListener('click', function () { cierraDialogo(dlg); });
      /* un clic en el fondo —no en la pieza— también cierra */
      dlg.addEventListener('click', function (ev) {
        if (ev.target === dlg || ev.target === caja) cierraDialogo(dlg);
      });
      alCerrar(dlg, function () {
        /* ⚠️ `close` llega un instante DESPUÉS de cerrar: si en ese instante
           ya se abrió otra pieza, limpiar aquí la borraría. */
        if (abierta(dlg)) return;
        var v = caja.querySelector('video');
        if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
        caja.innerHTML = '';
        bloqueo(false);
      });
    }

    /* 🔴 EL BLOQUEO DE SCROLL VA UN FOTOGRAMA DESPUÉS (2026-10-18). Poner o
       quitar `gf-visor-abierto` cambia el `overflow` de html, body y
       .page-wrapper, y eso rehace el diseño de TODA la ficha: 107-194 ms
       medidos (celular, CPU 4×), dentro del toque. Aplazándolo, el visor —que
       tapa la pantalla entera— se pinta primero, y el recálculo ocurre debajo,
       sin verse y fuera del INP. Al cerrar, igual: primero se ve la página.
       ⚠️ SOLO EN PANTALLA TÁCTIL. En escritorio la barra de scroll ocupa sitio:
          aplazar el desbloqueo dejaría la página un fotograma sin ella y daría
          un salto de ~15 px al cerrar. Allí se hace como siempre, al momento. */
    var tactil = matchMedia('(pointer: coarse)').matches;
    function bloqueo(si) {
      if (!tactil) { document.documentElement.classList.toggle('gf-visor-abierto', si); return; }
      requestAnimationFrame(function () {
        setTimeout(function () {
          if (si !== abierta(dlg)) return;   // ya cambió otra vez: no pisar
          document.documentElement.classList.toggle('gf-visor-abierto', si);
        }, 0);
      });
    }

    function grande(src) {
      try {
        var u = new URL(src, location.href);
        if (u.searchParams.has('width')) u.searchParams.set('width', '2000');
        return u.href;
      } catch (e) { return src; }
    }

    function abre(el) {
      if (!dlg) crea();
      caja.innerHTML = '';
      var pieza;
      if (el.tagName === 'VIDEO') {
        pieza = document.createElement('video');
        pieza.src = el.currentSrc || (el.querySelector('source') || {}).src || el.src;
        if (el.poster) pieza.poster = el.poster;
        pieza.controls = true;
        pieza.playsInline = true;
        pieza.loop = true;
        pieza.autoplay = true;
        /* si el navegador no deja con sonido, se reintenta en silencio */
        pieza.addEventListener('loadedmetadata', function () {
          var p = pieza.play();
          if (p && p.catch) p.catch(function () { pieza.muted = true; pieza.play(); });
        });
      } else {
        pieza = document.createElement('img');
        pieza.src = grande(el.currentSrc || el.src);
        pieza.alt = el.alt || '';
        pieza.decoding = 'async';
      }
      pieza.className = 'gf-visor__pieza';
      /* los reels son verticales: más bajos, para que no llenen la pantalla */
      if (el.closest('.gf-ev-reel')) pieza.classList.add('gf-visor__pieza--vertical');
      caja.appendChild(pieza);
      abreDialogo(dlg);
      bloqueo(true);
    }

    document.addEventListener('click', function (ev) {
      var el = ev.target.closest && ev.target.closest(SEL);
      if (!el || el.closest('.gf-visor')) return;
      /* los moldes no son contenido: no se abren */
      if (el.closest('.gf-ev-gal__molde, .gf-ev-reel--molde')) return;
      /* 🔴 LAS MINIATURAS NO ABREN EL VISOR (2026-09-27): su trabajo es volverse la foto
         principal. Abrían la pantalla completa encima y parecía que no habían cambiado nada. */
      if (el.closest('.gf-ev-gal__tira')) return;
      ev.preventDefault();
      abre(el);
    });
  }

  /* ══ LOS VÍDEOS SE VEN DONDE ESTÁN ═══════════════════════════════════════
     Pedido (2026-10-18): «que solo reproduzca desde ahí, y si se reproduce uno
     se para el resto».
       · Un toque lo reproduce CON SONIDO en su sitio; otro toque lo pausa. El
         toque es el gesto que el navegador exige para el sonido.
       · Cuando uno suena, se paran todos los demás y se marcan `data-gf-parado`:
         `autovideos()` ya no los arranca solos al volver a pantalla.
       · Un vídeo mudo que arranca solo NO para a nadie: si no, el de la galería
         cortaría el reel que se está viendo al pasar por delante.
     ⚠️ El telón de la home (`.gaela-telon__video`) no está en SEL: no se para.
     ⚠️ Si el vídeo trae controles nativos, el toque ya lo gestiona el navegador:
        aquí no se alterna, solo se escucha el `play` para parar al resto. */
  var VIDEOS = '.gf-ev-gal video, video.gf-ev-reel__v, video.gf-auto';
  function enSitio() {
    if (document.documentElement.dataset.gfEnSitio) return;
    document.documentElement.dataset.gfEnSitio = '1';

    document.addEventListener('click', function (ev) {
      var v = ev.target.closest && ev.target.closest(VIDEOS);
      if (!v || v.controls || v.closest('.gf-visor')) return;
      ev.preventDefault();
      if (!v.paused && !v.muted) { v.pause(); v.dataset.gfParado = '1'; return; }
      delete v.dataset.gfParado;
      /* Los demás se paran YA, con el toque: si el `play()` con sonido fallara y
         cayera al reintento mudo, el `play` de abajo no pararía a nadie. */
      paraLosDemas(v);
      if (v.preload === 'none' && v.dataset.gfHd && !v.src) v.src = v.dataset.gfHd;
      v.muted = false;
      var p = v.play();
      if (p && p.catch) p.catch(function () { v.muted = true; v.play(); });
    });

    function paraLosDemas(v) {
      [].forEach.call(document.querySelectorAll(VIDEOS), function (o) {
        if (o !== v && !o.paused) { o.pause(); o.dataset.gfParado = '1'; }
      });
    }

    /* Para los que llevan controles nativos, que no pasan por el clic de arriba.
       `play` no burbujea: se escucha en captura. */
    document.addEventListener('play', function (ev) {
      var v = ev.target;
      if (!v.matches || !v.matches(VIDEOS) || v.muted) return;
      paraLosDemas(v);
    }, true);
  }

  /* ══ LOS REELS SE MONTAN AL ACERCARSE (2026-10-19) ══════════════════════
     El bloque del reel manda su `<video>` dentro de un `<template>` para no
     pedir los pósters al cargar (ver `blocks/gaela-ev-reel.liquid`). Se
     montan cuando el CARRIL está a 900 px de la vista: al llegar ya están
     pintados, y el que nunca baja no los paga.
     ⚠️ Se vigila el carril, no cada reel: el observador recorta por el
     carril, y los reels que asoman a la derecha no «entrarían» hasta
     deslizar — se verían un instante en blanco. Al acercarse el carril se
     montan todos los suyos.
     Sin IntersectionObserver se montan todos de una vez. */
  function reels() {
    var lista = document.querySelectorAll('template[data-gf-reel]');
    if (!lista.length) return;
    /* Al montarlo se avisa con `gaela:reel` (burbujea): el play de
       `gaela-ev-en-video` le quita los controles nativos y le pone su botón. */
    function monta(t) {
      var copia = t.content.cloneNode(true);
      var v = copia.querySelector('video');
      t.replaceWith(copia);
      if (v) v.dispatchEvent(new CustomEvent('gaela:reel', { bubbles: true }));
    }
    if (!window.IntersectionObserver) { [].forEach.call(lista, monta); return; }
    var ojo = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        if (!e.isIntersecting) return;
        ojo.unobserve(e.target);
        [].forEach.call(e.target.querySelectorAll('template[data-gf-reel]'), monta);
      });
    }, { rootMargin: '900px 0px' });
    var carriles = [];
    [].forEach.call(lista, function (t) {
      var c = t.closest('.gf-riel') || t.closest('.gf-ev-reel').parentElement;
      if (carriles.indexOf(c) < 0) { carriles.push(c); ojo.observe(c); }
    });
  }

  function todo() { arrancar(); hoja(); bolsa(); cederAlPie(); rieles(); autovideos(); visor(); enSitio(); reels(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', todo);
  } else {
    todo();
  }

  document.addEventListener('shopify:section:load', todo);
})();
